import type { HSSEComplianceBriefing } from '../types.js';
import { serviceCountryName } from './serviceRequestCountries.js';

export const S15_FIELDS = [
    { key: 'organizationName', label: 'Organisation name', required: true },
    { key: 'industry', label: 'Industry and business activities', required: true },
    { key: 'country', label: 'Audit country', required: true, kind: 'country' },
    { key: 'state', label: 'State, province, or region' },
    { key: 'city', label: 'City' },
    { key: 'siteAddress', label: 'Site address and operations', kind: 'textarea' },
    { key: 'auditScope', label: 'Audit scope, concerns, and requirements', required: true, kind: 'textarea' },
] as const;
export type S15Intake = Record<typeof S15_FIELDS[number]['key'], string>;
const text = (value: unknown, maximum = 16000) => typeof value === 'string' ? value.trim().slice(0, maximum) : '';

export function cleanS15Intake(source: any): S15Intake {
    return Object.fromEntries(S15_FIELDS.map(field => [field.key,
        text(source?.[field.key], 'kind' in field && field.kind === 'textarea' ? 5000 : 240)])) as S15Intake;
}

export function prefillS15Intake(organization: any, service: any = {}): S15Intake {
    const address = organization?.headquartersAddress || {};
    return cleanS15Intake({ organizationName: organization?.name || organization?.brandName || service.organizationName,
        industry: organization?.industry, country: serviceCountryName(service.focusRegionCountry || address.country || address.countryCode),
        state: address.administrativeArea, city: address.city,
        siteAddress: [address.addressLine1, address.addressLine2, address.city, address.administrativeArea,
            serviceCountryName(address.country || address.countryCode)].filter(Boolean).join(', '),
        auditScope: service.description });
}

export const missingS15Intake = (intake: S15Intake) => S15_FIELDS
    .filter(field => 'required' in field && field.required && !intake[field.key]?.trim()).map(field => field.label);

export function cleanS15Report(source: any): HSSEComplianceBriefing {
    const rows = (key: string, fields: string[]) => (Array.isArray(source?.[key]) ? source[key] : []).slice(0, 100)
        .map((row: any) => Object.fromEntries(fields.map(field => [field, text(row?.[field])])));
    return { reportType: 'HSSEComplianceBriefing', executiveSummary: text(source?.executiveSummary),
        state: text(source?.state, 240), operationalContext: text(source?.operationalContext),
        regulatoryBodies: rows('regulatoryBodies', ['name', 'description', 'url']),
        keyRegulations: rows('keyRegulations', ['regulation', 'summary', 'url']),
        complianceChecklist: rows('complianceChecklist', ['item', 'description']),
        modelProgram: text(source?.modelProgram, 40000), disclaimer: text(source?.disclaimer),
        sources: rows('sources', ['title', 'uri']),
    } as HSSEComplianceBriefing;
}

export function s15ReportReady(report: HSSEComplianceBriefing): boolean {
    return Boolean(report.executiveSummary && report.operationalContext && report.modelProgram && report.disclaimer
        && report.regulatoryBodies.length && report.regulatoryBodies.every(row => row.name && row.description)
        && report.keyRegulations.length && report.keyRegulations.every(row => row.regulation && row.summary)
        && report.complianceChecklist.length && report.complianceChecklist.every(row => row.item && row.description));
}

export const s15DeliveryLink = (requestId: string, reference: string) => ({ id: `service-${requestId}-s15`,
    sourceId: `service-${requestId}-s15`, title: `${reference} HSSE Compliance Audit`, type: 'HSSEComplianceBriefing' });
